import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import {
  Button,
  Drawer,
  Empty,
  Input,
  InputNumber,
  Modal,
  Segmented,
  Select,
  Space,
  Switch,
  Tag,
  Tooltip,
  Upload,
  message,
  notification
} from "antd";
import {
  BookOutlined,
  DeleteOutlined,
  LockOutlined,
  PlusOutlined,
  SaveOutlined,
  UploadOutlined
} from "@ant-design/icons";
import QuestionEditor from "./QuestionEditor";
import BankPickerModal from "pages/FeedbackCapture/settings/BankPickerModal";
import useQuestionBank, { bankFields } from "pages/FeedbackCapture/settings/useQuestionBank";
import { createBankQuestion } from "util/questionBankApi";
import DistributeStep from "./DistributeStep";
import CloseSettings from "./CloseSettings";
import SurveyPreview from "./SurveyPreview";
import VoiceSurveyPreview from "./VoiceSurveyPreview";
import { makeBlankQuestion } from "./surveyService";
import {
  QUESTION_TYPE,
  QUESTION_TYPE_META,
  SURVEY_FLOW_MODE,
  SURVEY_RESPONSE_MODE,
  makeDefaultDistribution
} from "./constants";
import { APP_LANGUAGES, normalizeAppLanguage } from "util/locale";
import { getSurveyCapabilities } from "util/surveyCapabilities";
import {
  readBrandLogoDataUrl,
  validateBrandLogoFile
} from "./brandLogoHelpers";
import {
  applyLocaleToForm,
  buildTranslationsPayload,
  emptyLocaleContent,
  snapshotFormToLocale
} from "./surveyTranslations";
import styles from "./style.module.scss";

const { TextArea } = Input;
// Room for an introduction with a few numbered steps; the API stores any length.
const DESCRIPTION_MAX_LENGTH = 2000;

const defaultDistributionFor = responseMode => {
  const distribution = makeDefaultDistribution();
  // A new voice survey goes out by phone, so its one channel starts switched on.
  if (responseMode === SURVEY_RESPONSE_MODE.VOICE) {
    distribution.phone_call = { ...distribution.phone_call, enabled: true };
  }
  return distribution;
};

const emptyForm = (
  primaryLanguage = "en",
  responseMode = SURVEY_RESPONSE_MODE.TEXT
) => ({
  widgetHeaderTitle: "",
  name: "",
  description: "",
  questions: [],
  responseMode,
  distribution: defaultDistributionFor(responseMode),
  showBranding: true,
  requestContactDetails: false,
  contactDetailsRequired: false,
  requestConsent: false,
  startConsent: false,
  startConsentText: "",
  startConsentPrivacyUrl: "",
  startConsentVersion: 0,
  oneResponsePerEmail: false,
  closeEnabled: false,
  closeLocal: "",
  closeTimezone: "Europe/Istanbul",
  closeMaxResponses: null,
  closedMessage: "",
  showDeadline: false,
  closeReminder: false,
  closeGraceMinutes: 60,
  autoClosed: false,
  videoRetentionDays: 90,
  videoTranscript: false,
  videoContactEmail: "",
  primaryColor: "#6366f1",
  logoUrl: "",
  popupPosition: "bottom_right",
  flowMode: SURVEY_FLOW_MODE.STEPPED,
  thankYouMessage: "",
  multilingualEnabled: false,
  primaryLanguage,
  editingLanguage: primaryLanguage,
  translations: {}
});

// Create / edit flow for a survey. Rendered as an antd Drawer (side sheet) so the
// list stays in context. Owns the full builder state and runs validation before
// handing a clean payload back to the parent via onSave.
const SurveyBuilderDrawer = ({
  open,
  survey,
  saving,
  formatType = "text",
  // How a new survey is answered; an existing one keeps its own.
  responseMode: newResponseMode = SURVEY_RESPONSE_MODE.TEXT,
  onClose,
  onSave,
  onUpgrade,
  // Video answer questions are offered only while they are on for the organisation.
  videoEnabled = false,
  // "distribute" opens an existing survey straight on its Distribute step.
  initialStep = "build"
}) => {
  const { t, i18n } = useTranslation();
  const subscriptionStatus = useSelector(state => state.userReducer);
  const caps = useMemo(() => getSurveyCapabilities(subscriptionStatus), [
    subscriptionStatus
  ]);
  const hasSurveyPlus = caps.canBrand;
  const defaultLanguage = normalizeAppLanguage(i18n.language);
  const isEdit = !!survey;
  const [form, setForm] = useState(emptyForm());
  const [errors, setErrors] = useState({ name: null, questions: {} });
  const [step, setStep] = useState("build");
  const [activeQuestionIndex, setActiveQuestionIndex] = useState(null);
  const [previewPrimaryColor, setPreviewPrimaryColor] = useState("#6366f1");

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setPreviewPrimaryColor(form.primaryColor || "#6366f1");
    }, 200);
    return () => window.clearTimeout(timer);
  }, [form.primaryColor]);

  // Re-seed the local form whenever the drawer is (re)opened for a survey.
  useEffect(() => {
    if (!open) return;
    if (survey) {
      setForm({
        widgetHeaderTitle: survey.widgetHeaderTitle || "",
        name: survey.name || "",
        description: survey.description || "",
        questions: (survey.questions || []).map(q => ({ ...q })),
        responseMode: survey.responseMode || SURVEY_RESPONSE_MODE.TEXT,
        distribution: survey.distribution
          ? { ...makeDefaultDistribution(), ...survey.distribution }
          : makeDefaultDistribution(),
        showBranding: survey.showBranding !== false,
        requestContactDetails: survey.requestContactDetails === true,
        contactDetailsRequired: survey.contactDetailsRequired === true,
        requestConsent: survey.requestConsent === true,
        startConsent: survey.startConsent === true,
        startConsentText: survey.startConsentText || "",
        startConsentPrivacyUrl: survey.startConsentPrivacyUrl || "",
        startConsentVersion: survey.startConsentVersion || 0,
        oneResponsePerEmail: survey.oneResponsePerEmail === true,
        closeEnabled: survey.closeEnabled === true,
        closeLocal: survey.closeLocal || "",
        closeTimezone: survey.closeTimezone || "Europe/Istanbul",
        closeMaxResponses: survey.closeMaxResponses || null,
        closedMessage: survey.closedMessage || "",
        showDeadline: survey.showDeadline === true,
        closeReminder: survey.closeReminder === true,
        closeGraceMinutes: survey.closeGraceMinutes ?? 60,
        autoClosed: survey.autoClosed === true,
        videoRetentionDays: survey.videoRetentionDays || 90,
        videoTranscript: survey.videoTranscript === true,
        videoContactEmail: survey.videoContactEmail || "",
        primaryColor: survey.primaryColor || "#6366f1",
        logoUrl: survey.logoUrl || "",
        popupPosition: survey.popupPosition || "bottom_right",
        flowMode: survey.flowMode || SURVEY_FLOW_MODE.STEPPED,
        thankYouMessage: survey.thankYouMessage || "",
        multilingualEnabled: survey.multilingualEnabled === true,
        primaryLanguage: survey.primaryLanguage || defaultLanguage,
        editingLanguage: survey.primaryLanguage || defaultLanguage,
        translations: survey.translations || {}
      });
    } else {
      setForm(emptyForm(defaultLanguage, newResponseMode));
    }
    setStep(survey && initialStep === "distribute" ? "distribute" : "build");
    setActiveQuestionIndex(null);
    setErrors({ name: null, questions: {} });
  }, [open, survey, defaultLanguage, newResponseMode, initialStep]);

  const questionCount = form.questions.length;

  const languageOptions = useMemo(
    () =>
      APP_LANGUAGES.map(lang => ({
        value: lang.code,
        label: lang.nativeLabel
      })),
    []
  );

  const [previewOpen, setPreviewOpen] = useState(false);
  // The company question bank: questions linked into this survey follow it.
  const [bank, reloadBank] = useQuestionBank(open);
  const [bankPickerOpen, setBankPickerOpen] = useState(false);
  const bankUsage = useMemo(() => {
    const out = {};
    bank.questions.forEach((bq) => {
      out[bq.id] = (bq.used_in || []).length;
    });
    return out;
  }, [bank.questions]);

  const setField = (field, value) =>
    setForm(prev => ({ ...prev, [field]: value }));

  const persistCurrentLocale = prev => {
    if (!prev.multilingualEnabled) return prev.translations || {};
    return {
      ...(prev.translations || {}),
      [prev.editingLanguage]: snapshotFormToLocale(prev)
    };
  };

  const handleMultilingualToggle = checked => {
    if (!hasSurveyPlus) return;
    setForm(prev => {
      if (!checked) {
        const translations = persistCurrentLocale(prev);
        const primaryLocale =
          translations[prev.primaryLanguage] || snapshotFormToLocale(prev);
        return {
          ...applyLocaleToForm(prev, primaryLocale),
          multilingualEnabled: false,
          editingLanguage: prev.primaryLanguage,
          translations
        };
      }
      const translations = persistCurrentLocale(prev);
      translations[prev.primaryLanguage] = snapshotFormToLocale(prev);
      return {
        ...prev,
        multilingualEnabled: true,
        translations,
        editingLanguage: prev.primaryLanguage
      };
    });
  };

  const handleEditingLanguageChange = nextLang => {
    setForm(prev => {
      const translations = persistCurrentLocale(prev);
      const nextLocale = translations[nextLang] || emptyLocaleContent();
      return {
        ...applyLocaleToForm(prev, nextLocale),
        translations,
        editingLanguage: nextLang
      };
    });
  };

  const updateQuestion = (index, next) =>
    setForm(prev => {
      const questions = [...prev.questions];
      questions[index] = next;
      return { ...prev, questions };
    });

  const addQuestion = () =>
    setForm(prev => ({
      ...prev,
      questions: [...prev.questions, makeBlankQuestion()]
    }));

  // Bank questions are added and created in the survey's own language; other languages
  // get the bank's translations when the survey is saved.
  const editingPrimary = !form.multilingualEnabled || form.editingLanguage === form.primaryLanguage;

  const addFromBank = bankQuestion => {
    setForm(prev => ({
      ...prev,
      questions: [
        ...prev.questions,
        { ...makeBlankQuestion(), options: [], ...bankQuestion.question, bankId: bankQuestion.id }
      ]
    }));
    setBankPickerOpen(false);
  };

  const addToBank = async question => {
    const translations = {};
    Object.entries(form.translations || {}).forEach(([lang, locale]) => {
      const local = locale?.questions?.[question.id];
      if (lang !== form.primaryLanguage && local?.text) {
        translations[lang] = { text: local.text, options: local.options || [] };
      }
    });
    const res = await createBankQuestion(bankFields(question), translations);
    if (!res || res.error) {
      notification.error({ message: t("surveyManagement.bank.add_failed"), placement: "bottomRight" });
      return;
    }
    setForm(prev => ({
      ...prev,
      questions: prev.questions.map(q => (q.id === question.id ? { ...q, bankId: res.id } : q))
    }));
    reloadBank();
    notification.success({ message: t("surveyManagement.bank.added_to_bank"), placement: "bottomRight" });
  };

  const removeQuestion = index =>
    setForm(prev => ({
      ...prev,
      questions: prev.questions.filter((_, i) => i !== index)
    }));

  const moveQuestion = (index, direction) =>
    setForm(prev => {
      const target = index + direction;
      if (target < 0 || target >= prev.questions.length) return prev;
      const questions = [...prev.questions];
      const [moved] = questions.splice(index, 1);
      questions.splice(target, 0, moved);
      return { ...prev, questions };
    });

  const validate = () => {
    const nextErrors = { name: null, questions: {} };
    const primaryLanguage = normalizeAppLanguage(form.primaryLanguage);
    let validationForm = form;

    if (form.multilingualEnabled) {
      const translations = persistCurrentLocale(form);
      const primaryLocale =
        translations[primaryLanguage] || snapshotFormToLocale(form);
      validationForm = applyLocaleToForm(form, primaryLocale);
    }

    if (!validationForm.name.trim()) {
      nextErrors.name = t("surveyManagement.err_title_required");
    }
    if (validationForm.questions.length === 0) {
      nextErrors.formLevel = t("surveyManagement.err_add_question");
    }
    if (form.closeEnabled && !form.closeLocal && !form.closeMaxResponses) {
      nextErrors.closeLocal = t("surveyManagement.close.needs_rule");
    }
    if (form.startConsent && !(form.startConsentText || "").trim()) {
      nextErrors.startConsentText = t("surveyManagement.start_consent_text_required");
    }
    validationForm.questions.forEach((q, index) => {
      const meta = QUESTION_TYPE_META[q.type] || {};
      if (!q.text.trim()) {
        nextErrors.questions[index] = t("surveyManagement.err_question_text");
        return;
      }
      if (meta.hasOptions) {
        const filled = (q.options || []).filter(o => o.trim());
        if (filled.length < 2) {
          nextErrors.questions[index] = t("surveyManagement.err_two_options");
        }
      }
    });
    setErrors(nextErrors);
    const hasQuestionError = Object.keys(nextErrors.questions).length > 0;
    return (
      !nextErrors.name &&
      !nextErrors.formLevel &&
      !nextErrors.startConsentText &&
      !nextErrors.closeLocal &&
      !hasQuestionError
    );
  };

  const handleSave = () => {
    if (!validate()) {
      setStep("build");
      message.error(t("surveyManagement.err_fix_fields"));
      return;
    }

    const {
      primaryLanguage,
      translations,
      primaryLocale
    } = buildTranslationsPayload(form);
    const payloadForm = form.multilingualEnabled
      ? applyLocaleToForm(form, primaryLocale)
      : form;

    onSave({
      widgetHeaderTitle: payloadForm.widgetHeaderTitle.trim(),
      name: payloadForm.name.trim(),
      description: payloadForm.description.trim(),
      questions: payloadForm.questions,
      responseMode: form.responseMode,
      distribution: form.distribution,
      showBranding: caps.canRemoveWatermark
        ? form.showBranding !== false
        : true,
      requestContactDetails: !!form.requestContactDetails,
      contactDetailsRequired: form.requestContactDetails
        ? form.contactDetailsRequired === true
        : false,
      requestConsent: !!form.requestConsent && !form.startConsent,
      startConsent: !!form.startConsent,
      startConsentText: (form.startConsentText || "").trim(),
      startConsentPrivacyUrl: (form.startConsentPrivacyUrl || "").trim(),
      oneResponsePerEmail: !!form.oneResponsePerEmail,
      closeEnabled: !!form.closeEnabled,
      closeLocal: form.closeLocal,
      closeTimezone: form.closeTimezone,
      closeMaxResponses: form.closeMaxResponses,
      closedMessage: form.closedMessage,
      showDeadline: !!form.showDeadline,
      closeReminder: !!form.closeReminder,
      closeGraceMinutes: form.closeGraceMinutes,
      videoRetentionDays: form.videoRetentionDays,
      videoTranscript: !!form.videoTranscript,
      videoContactEmail: (form.videoContactEmail || "").trim(),
      primaryColor: hasSurveyPlus ? form.primaryColor : undefined,
      logoUrl: hasSurveyPlus ? form.logoUrl : undefined,
      popupPosition: hasSurveyPlus ? form.popupPosition : undefined,
      flowMode: form.flowMode || SURVEY_FLOW_MODE.STEPPED,
      thankYouMessage: (form.thankYouMessage || "").trim(),
      multilingualEnabled: !!form.multilingualEnabled && hasSurveyPlus,
      primaryLanguage,
      editingLanguage: form.editingLanguage,
      translations: form.multilingualEnabled ? translations : {}
    });
  };

  const enabledChannels = Object.values(form.distribution || {}).filter(
    c => c && c.enabled
  ).length;

  const isVoice = form.responseMode === SURVEY_RESPONSE_MODE.VOICE;
  const title = useMemo(() => {
    if (isVoice) {
      return isEdit
        ? t("surveyManagement.edit_voice_survey")
        : t("surveyManagement.create_voice_survey");
    }
    return isEdit
      ? t("surveyManagement.edit_survey")
      : t("surveyManagement.create_survey");
  }, [isEdit, isVoice, t]);

  const previewDisplayMode =
    form.distribution?.web_embed?.enabled &&
    form.distribution.web_embed.displayMode
      ? form.distribution.web_embed.displayMode
      : "popup";

  const handleLogoUpload = async file => {
    const errorCode = validateBrandLogoFile(file);
    if (errorCode === "format") {
      message.error(t("surveyManagement.brand_logo_error_format"));
      return Upload.LIST_IGNORE;
    }
    if (errorCode === "size") {
      message.error(t("surveyManagement.brand_logo_error_size"));
      return Upload.LIST_IGNORE;
    }
    try {
      const dataUrl = await readBrandLogoDataUrl(file);
      setField("logoUrl", dataUrl);
    } catch (_) {
      message.error(t("surveyManagement.brand_logo_error_read"));
    }
    return false;
  };

  const previewProps = {
    name: form.name,
    description: form.description,
    questions: form.questions,
    displayMode: previewDisplayMode,
    widgetHeaderTitle: form.widgetHeaderTitle,
    showBranding: form.showBranding !== false,
    requestContactDetails: !!form.requestContactDetails,
    contactDetailsRequired: form.contactDetailsRequired === true,
    primaryColor: previewPrimaryColor,
    logoUrl: form.logoUrl,
    popupPosition: form.popupPosition,
    flowMode: form.flowMode,
    thankYouMessage: form.thankYouMessage,
    formatType,
  };

  return (
    <Drawer
      title={title}
      width={980}
      open={open}
      onClose={onClose}
      destroyOnClose
      className={styles.builderDrawer}
      rootClassName={styles.builderDrawerRoot}
      footer={
        <div className={styles.drawerFooter}>
          <Space>
            <Button onClick={onClose}>{t("surveyManagement.cancel")}</Button>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              loading={saving}
              onClick={handleSave}
            >
              {isEdit
                ? t("surveyManagement.save_changes")
                : t("surveyManagement.create_survey_action")}
            </Button>
          </Space>
        </div>
      }
    >
      <div className={styles.builderSteps}>
        <Segmented
          block
          value={step}
          onChange={setStep}
          options={[
            {
              value: "build",
              label: t("surveyManagement.step_build")
            },
            {
              value: "distribute",
              label:
                enabledChannels > 0
                  ? `${t(
                      "surveyManagement.step_distribute"
                    )} (${enabledChannels})`
                  : t("surveyManagement.step_distribute")
            }
          ]}
        />
      </div>

      <div className={styles.drawerLayout}>
        <div className={styles.drawerFormCol}>
          {step === "distribute" ? (
            <DistributeStep
              instrumentId={survey?.id}
              surveyTitle={form.name}
              questions={form.questions}
              distribution={form.distribution}
              onChange={distribution => setField("distribution", distribution)}
              capabilities={caps}
              onUpgrade={onUpgrade}
              responseMode={form.responseMode}
              surveyLanguage={form.primaryLanguage}
            />
          ) : (
            <>
              <div className={styles.builderSection}>
                <div className={styles.settingRow}>
                  <div className={styles.settingRowText}>
                    <span className={styles.fieldLabel}>
                      {t("surveyManagement.multilingual_title")}{" "}
                      <Tag color="purple" className={styles.plusTag}>
                        {t("surveyManagement.survey_plus_badge")}
                      </Tag>
                    </span>
                    <span className={styles.fieldHint}>
                      {hasSurveyPlus
                        ? t("surveyManagement.multilingual_hint")
                        : t("surveyManagement.multilingual_locked_hint")}
                    </span>
                  </div>
                  <Tooltip
                    title={
                      hasSurveyPlus
                        ? null
                        : t("surveyManagement.multilingual_locked_tooltip")
                    }
                  >
                    <Switch
                      checked={!!form.multilingualEnabled && hasSurveyPlus}
                      disabled={!hasSurveyPlus}
                      onChange={handleMultilingualToggle}
                    />
                  </Tooltip>
                </div>
                {!hasSurveyPlus ? (
                  <div className={styles.plusLockNote}>
                    <LockOutlined />
                    <span>
                      {t("surveyManagement.multilingual_locked_note")}
                    </span>
                    {onUpgrade ? (
                      <Button
                        type="link"
                        size="small"
                        onClick={() => onUpgrade("plus")}
                      >
                        {t("surveyManagement.upgrade_plan_cta")}
                      </Button>
                    ) : null}
                  </div>
                ) : null}
              </div>

              {form.multilingualEnabled && hasSurveyPlus ? (
                <div className={styles.builderSection}>
                  <label className={styles.fieldLabel}>
                    {t("surveyManagement.editing_language")}
                  </label>
                  <Segmented
                    block
                    value={form.editingLanguage}
                    onChange={handleEditingLanguageChange}
                    options={languageOptions}
                  />
                  {form.editingLanguage === form.primaryLanguage ? (
                    <p className={styles.fieldHint}>
                      {t("surveyManagement.primary_language_hint")}
                    </p>
                  ) : (
                    <p className={styles.fieldHint}>
                      {t("surveyManagement.translation_language_hint")}
                    </p>
                  )}
                </div>
              ) : (
                <div className={styles.builderSection}>
                  <label className={styles.fieldLabel}>
                    {t("surveyManagement.survey_language")}
                  </label>
                  <Select
                    value={form.primaryLanguage}
                    onChange={lang =>
                      setForm(prev => ({
                        ...prev,
                        primaryLanguage: lang,
                        editingLanguage: lang
                      }))
                    }
                    options={languageOptions}
                    style={{ width: "100%" }}
                  />
                  <p className={styles.fieldHint}>
                    {t("surveyManagement.survey_language_hint")}
                  </p>
                </div>
              )}

              <div className={styles.builderSection}>
                <label className={styles.fieldLabel}>
                  {t("surveyManagement.field_widget_header_title")}
                </label>
                <Input
                  value={form.widgetHeaderTitle}
                  onChange={e => setField("widgetHeaderTitle", e.target.value)}
                  placeholder={t(
                    "surveyManagement.field_widget_header_title_placeholder"
                  )}
                  maxLength={80}
                />
              </div>

              <div className={styles.builderSection}>
                <label className={styles.fieldLabel}>
                  {t("surveyManagement.field_title")}{" "}
                  <span className={styles.requiredMark}>*</span>
                </label>
                <Input
                  value={form.name}
                  onChange={e => setField("name", e.target.value)}
                  placeholder={t("surveyManagement.field_title_placeholder")}
                  status={errors.name ? "error" : ""}
                  maxLength={120}
                />
                {errors.name && (
                  <div className={styles.fieldError}>{errors.name}</div>
                )}
              </div>

              <div className={styles.builderSection}>
                <label className={styles.fieldLabel}>
                  {t("surveyManagement.field_description")}
                </label>
                <TextArea
                  value={form.description}
                  onChange={e => setField("description", e.target.value)}
                  placeholder={t(
                    "surveyManagement.field_description_placeholder"
                  )}
                  autoSize={{ minRows: 2, maxRows: 12 }}
                  maxLength={DESCRIPTION_MAX_LENGTH}
                  showCount
                />
              </div>

              <div className={styles.builderSection}>
                <div className={styles.settingRow}>
                  <div className={styles.settingRowText}>
                    <span className={styles.fieldLabel}>
                      {t("surveyManagement.request_contact_details")}
                    </span>
                    <span className={styles.fieldHint}>
                      {t("surveyManagement.request_contact_details_hint")}
                    </span>
                  </div>
                  <Switch
                    checked={!!form.requestContactDetails}
                    onChange={checked =>
                      setForm(prev => ({
                        ...prev,
                        requestContactDetails: checked,
                        contactDetailsRequired: checked
                          ? prev.contactDetailsRequired === true
                          : false
                      }))
                    }
                  />
                </div>
                {form.requestContactDetails ? (
                  <div className={styles.contactModePanel}>
                    <div className={styles.contactModeHeader}>
                      <span className={styles.contactModeLabel}>
                        {t("surveyManagement.contact_details_mode_label")}
                      </span>
                      <span className={styles.contactModeHint}>
                        {t("surveyManagement.contact_details_mode_hint")}
                      </span>
                    </div>
                    <Segmented
                      block
                      size="large"
                      className={styles.contactModeSegmented}
                      value={
                        form.contactDetailsRequired === true
                          ? "required"
                          : "optional"
                      }
                      onChange={value =>
                        setField("contactDetailsRequired", value === "required")
                      }
                      options={[
                        {
                          label: t("surveyManagement.contact_details_optional"),
                          value: "optional"
                        },
                        {
                          label: t("surveyManagement.contact_details_required"),
                          value: "required"
                        }
                      ]}
                    />
                  </div>
                ) : null}
              </div>

              <div className={styles.builderSection}>
                <div className={styles.settingRow}>
                  <div className={styles.settingRowText}>
                    <span className={styles.fieldLabel}>
                      {t("surveyManagement.request_consent")}
                    </span>
                    <span className={styles.fieldHint}>
                      {t("surveyManagement.request_consent_hint")}
                    </span>
                  </div>
                  <Switch
                    checked={!!form.requestConsent && !form.startConsent}
                    onChange={checked => {
                      setField("requestConsent", checked);
                      // One consent box per survey: at submit or before starting.
                      if (checked) setField("startConsent", false);
                    }}
                  />
                </div>
              </div>

              <div className={styles.builderSection}>
                <div className={styles.settingRow}>
                  <div className={styles.settingRowText}>
                    <span className={styles.fieldLabel}>
                      {t("surveyManagement.start_consent")}
                    </span>
                    <span className={styles.fieldHint}>
                      {t("surveyManagement.start_consent_hint")}
                    </span>
                  </div>
                  <Switch
                    checked={!!form.startConsent}
                    onChange={checked => {
                      setField("startConsent", checked);
                      if (checked) {
                        setField("requestConsent", false);
                        if (!(form.startConsentText || "").trim()) {
                          setField("startConsentText", t("surveyManagement.start_consent_default"));
                        }
                      }
                    }}
                  />
                </div>
                {form.startConsent ? (
                  <div className={styles.startConsentFields}>
                    <label className={styles.fieldLabel} htmlFor="start-consent-text">
                      {t("surveyManagement.start_consent_text_label")}
                    </label>
                    <TextArea
                      id="start-consent-text"
                      value={form.startConsentText}
                      onChange={e => setField("startConsentText", e.target.value)}
                      autoSize={{ minRows: 3, maxRows: 8 }}
                      maxLength={2000}
                      status={errors.startConsentText ? "error" : ""}
                    />
                    {errors.startConsentText && (
                      <div className={styles.fieldError}>{errors.startConsentText}</div>
                    )}
                    <label className={styles.fieldLabel} htmlFor="start-consent-url">
                      {t("surveyManagement.start_consent_url_label")}
                    </label>
                    <Input
                      id="start-consent-url"
                      value={form.startConsentPrivacyUrl}
                      onChange={e => setField("startConsentPrivacyUrl", e.target.value)}
                      placeholder="https://"
                      maxLength={500}
                    />
                    <span className={styles.fieldHint}>
                      {form.startConsentVersion
                        ? t("surveyManagement.start_consent_version", { version: form.startConsentVersion })
                        : t("surveyManagement.start_consent_version_new")}
                    </span>
                  </div>
                ) : null}
              </div>

              <div className={styles.builderSection}>
                <div className={styles.settingRow}>
                  <div className={styles.settingRowText}>
                    <span className={styles.fieldLabel}>
                      {t("surveyManagement.one_response_per_email")}
                    </span>
                    <span className={styles.fieldHint}>
                      {t("surveyManagement.one_response_per_email_hint")}
                    </span>
                  </div>
                  <Switch
                    checked={!!form.oneResponsePerEmail}
                    onChange={checked =>
                      setField("oneResponsePerEmail", checked)
                    }
                  />
                </div>
              </div>

              <CloseSettings form={form} setField={setField} error={errors.closeLocal} />

              {form.questions.some(q => q.type === QUESTION_TYPE.VIDEO) ? (
                <div className={styles.builderSection}>
                  <span className={styles.fieldLabel}>
                    {t("surveyManagement.video_settings")}
                  </span>
                  <div className={styles.settingRow}>
                    <div className={styles.settingRowText}>
                      <span className={styles.fieldLabel}>
                        {t("surveyManagement.video_retention_days")}
                      </span>
                      <span className={styles.fieldHint}>
                        {t("surveyManagement.video_retention_days_hint")}
                      </span>
                    </div>
                    <InputNumber
                      min={1}
                      max={365}
                      value={form.videoRetentionDays}
                      onChange={value =>
                        setField("videoRetentionDays", value || 90)
                      }
                    />
                  </div>
                  <div className={styles.settingRow}>
                    <div className={styles.settingRowText}>
                      <span className={styles.fieldLabel}>
                        {t("surveyManagement.video_transcript")}
                      </span>
                      <span className={styles.fieldHint}>
                        {t("surveyManagement.video_transcript_hint")}
                      </span>
                    </div>
                    <Switch
                      checked={!!form.videoTranscript}
                      onChange={checked => setField("videoTranscript", checked)}
                    />
                  </div>
                  <label className={styles.fieldLabel}>
                    {t("surveyManagement.video_contact_email")}
                  </label>
                  <Input
                    type="email"
                    value={form.videoContactEmail}
                    onChange={e =>
                      setField("videoContactEmail", e.target.value)
                    }
                    placeholder={t(
                      "surveyManagement.video_contact_email_placeholder"
                    )}
                  />
                </div>
              ) : null}

              <div className={styles.builderSection}>
                <label className={styles.fieldLabel}>
                  {t("surveyManagement.flow_mode_label")}
                </label>
                <Segmented
                  value={form.flowMode || SURVEY_FLOW_MODE.STEPPED}
                  onChange={value => setField("flowMode", value)}
                  options={[
                    {
                      label: t("surveyManagement.flow_mode_stepped"),
                      value: SURVEY_FLOW_MODE.STEPPED
                    },
                    {
                      label: t("surveyManagement.flow_mode_single_page"),
                      value: SURVEY_FLOW_MODE.SINGLE_PAGE
                    }
                  ]}
                />
                <p className={styles.fieldHint}>
                  {t("surveyManagement.flow_mode_hint")}
                </p>
                <label className={styles.fieldLabel}>
                  {t("surveyManagement.thank_you_message_label")}
                </label>
                <TextArea
                  rows={2}
                  value={form.thankYouMessage || ""}
                  onChange={e => setField("thankYouMessage", e.target.value)}
                  placeholder={t(
                    "surveyManagement.thank_you_message_placeholder"
                  )}
                  maxLength={280}
                />
              </div>

              <div className={styles.builderSection}>
                <div className={styles.settingRow}>
                  <div className={styles.settingRowText}>
                    <span className={styles.fieldLabel}>
                      {t("surveyManagement.brand_title")}{" "}
                      <Tag color="purple" className={styles.plusTag}>
                        {t("surveyManagement.survey_plus_badge")}
                      </Tag>
                    </span>
                    <span className={styles.fieldHint}>
                      {hasSurveyPlus
                        ? t("surveyManagement.brand_hint")
                        : t("surveyManagement.brand_locked_hint")}
                    </span>
                  </div>
                </div>
                {hasSurveyPlus ? (
                  <div className={styles.brandFields}>
                    <label className={styles.fieldLabel}>
                      {t("surveyManagement.brand_primary_color")}
                    </label>
                    <div className={styles.colorPickerRow}>
                      <input
                        type="color"
                        value={form.primaryColor || "#6366f1"}
                        onChange={e => setField("primaryColor", e.target.value)}
                        className={styles.colorPickerInput}
                        aria-label={t("surveyManagement.brand_primary_color")}
                      />
                      <Input
                        value={form.primaryColor || "#6366f1"}
                        onChange={e => setField("primaryColor", e.target.value)}
                        maxLength={7}
                      />
                    </div>
                    <label className={styles.fieldLabel}>
                      {t("surveyManagement.brand_logo_upload")}
                    </label>
                    <p className={styles.fieldHint}>
                      {t("surveyManagement.brand_logo_upload_hint")}
                    </p>
                    <div className={styles.logoUploadRow}>
                      {form.logoUrl ? (
                        <div className={styles.logoPreviewWrap}>
                          <img
                            src={form.logoUrl}
                            alt=""
                            className={styles.logoPreviewImage}
                            draggable={false}
                          />
                          <Button
                            type="text"
                            size="small"
                            icon={<DeleteOutlined />}
                            onClick={() => setField("logoUrl", "")}
                          >
                            {t("surveyManagement.brand_logo_remove")}
                          </Button>
                        </div>
                      ) : null}
                      <Upload
                        accept=".png,.svg,image/png,image/svg+xml"
                        showUploadList={false}
                        beforeUpload={handleLogoUpload}
                      >
                        <Button icon={<UploadOutlined />}>
                          {t("surveyManagement.brand_logo_upload_action")}
                        </Button>
                      </Upload>
                    </div>
                    <label className={styles.fieldLabel}>
                      {t("surveyManagement.brand_logo_url")}
                    </label>
                    <Input
                      value={form.logoUrl}
                      onChange={e => setField("logoUrl", e.target.value)}
                      placeholder={t(
                        "surveyManagement.brand_logo_url_placeholder"
                      )}
                      maxLength={500}
                    />
                    <label className={styles.fieldLabel}>
                      {t("surveyManagement.brand_popup_position")}
                    </label>
                    <Segmented
                      block
                      value={form.popupPosition || "bottom_right"}
                      onChange={value => setField("popupPosition", value)}
                      options={[
                        {
                          label: t("surveyManagement.brand_popup_bottom_right"),
                          value: "bottom_right"
                        },
                        {
                          label: t("surveyManagement.brand_popup_bottom_left"),
                          value: "bottom_left"
                        }
                      ]}
                    />
                    <div
                      className={styles.settingRow}
                      style={{ marginTop: 16 }}
                    >
                      <div className={styles.settingRowText}>
                        <span className={styles.fieldLabel}>
                          {t("surveyManagement.watermark_remove_label")}
                        </span>
                        <span className={styles.fieldHint}>
                          {caps.canRemoveWatermark
                            ? t("surveyManagement.watermark_remove_hint")
                            : t("surveyManagement.watermark_locked_hint")}
                        </span>
                      </div>
                      <Switch
                        checked={form.showBranding === false}
                        disabled={!caps.canRemoveWatermark}
                        onChange={checked => setField("showBranding", !checked)}
                      />
                    </div>
                  </div>
                ) : (
                  <div className={styles.plusLockNote}>
                    <LockOutlined />
                    <span>{t("surveyManagement.brand_locked_note")}</span>
                    {onUpgrade ? (
                      <Button
                        type="link"
                        size="small"
                        onClick={() => onUpgrade("plus")}
                      >
                        {t("surveyManagement.upgrade_plan_cta")}
                      </Button>
                    ) : null}
                  </div>
                )}
              </div>

              <div className={styles.builderSection}>
                <div className={styles.questionsHeader}>
                  <span className={styles.fieldLabel}>
                    {t("surveyManagement.questions")}{" "}
                    <span className={styles.questionCountBadge}>
                      {questionCount}
                    </span>
                  </span>
                  <Space size={8}>
                    {bank.questions.length > 0 && editingPrimary ? (
                      <Button
                        type="dashed"
                        icon={<BookOutlined />}
                        onClick={() => setBankPickerOpen(true)}
                        size="small"
                      >
                        {t("surveyManagement.bank.add_from_bank")}
                      </Button>
                    ) : null}
                    <Button
                      type="dashed"
                      icon={<PlusOutlined />}
                      onClick={addQuestion}
                      size="small"
                    >
                      {t("surveyManagement.add_question")}
                    </Button>
                  </Space>
                </div>

                {errors.formLevel && (
                  <div className={styles.fieldError}>{errors.formLevel}</div>
                )}

                {questionCount === 0 ? (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={t("surveyManagement.no_questions")}
                    className={styles.builderEmpty}
                  >
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={addQuestion}
                    >
                      {t("surveyManagement.add_first_question")}
                    </Button>
                  </Empty>
                ) : (
                  <div className={styles.questionsList}>
                    {form.questions.map((q, index) => (
                      <QuestionEditor
                        key={q.id}
                        question={q}
                        index={index}
                        total={questionCount}
                        isActive={activeQuestionIndex === index}
                        onFocus={() => setActiveQuestionIndex(index)}
                        error={errors.questions[index]}
                        onChange={next => updateQuestion(index, next)}
                        onRemove={() => removeQuestion(index)}
                        onMoveUp={() => moveQuestion(index, -1)}
                        onMoveDown={() => moveQuestion(index, 1)}
                        videoEnabled={videoEnabled}
                        voice={isVoice}
                        bankUsage={q.bankId ? bankUsage[q.bankId] || 0 : 0}
                        onAddToBank={bank.can_manage && editingPrimary ? addToBank : undefined}
                      />
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
        <div className={styles.drawerPreviewCol}>
          {isVoice ? (
            <VoiceSurveyPreview
              questions={form.questions}
              greeting={form.distribution?.phone_call?.greeting || survey?.defaultCallGreeting}
              disclosure={form.distribution?.phone_call?.disclosure}
              thankYouMessage={form.thankYouMessage}
            />
          ) : (
            <SurveyPreview {...previewProps} onExpand={() => setPreviewOpen(true)} />
          )}
        </div>
      </div>
      <BankPickerModal
        open={bankPickerOpen}
        questions={bank.questions}
        linkedIds={form.questions.map(q => Number(q.bankId)).filter(Boolean)}
        onPick={addFromBank}
        onCancel={() => setBankPickerOpen(false)}
      />
      <Modal
        open={previewOpen && !isVoice}
        onCancel={() => setPreviewOpen(false)}
        footer={null}
        width={1000}
        centered
        destroyOnClose
        title={t("surveyManagement.preview_title")}
        className={styles.previewModal}
      >
        <SurveyPreview {...previewProps} expanded />
      </Modal>
    </Drawer>
  );
};

export default SurveyBuilderDrawer;
